import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from 'lib/utils'
import { Loader2 } from 'lucide-react'
import * as React from 'react'
import { Tooltip } from './tooltip'

// Shared hover treatment for the brand-forward variants: Plivo blue fill plus a
// subtle silver glint sweeping left→right, reusing the sidebar logo's animation
// and band geometry (see blocks/sidebar/components/header.tsx).
//
// The foreground is pinned to white rather than inherited: `default` inverts in
// dark mode (white bg / navy text), so keeping text-primary-foreground would
// land navy-on-blue at 2.75:1. White on brand is 6.49:1. For the same reason the
// band is white in both themes — the hover surface is always blue, unlike the
// logo's light/dark surface.
//
// The glint is an ::after, not a child node, so `asChild`/Slot still receives
// exactly one child.
const brandHoverGlint = cn(
  'hover:bg-brand hover:text-brand-foreground',
  'relative overflow-hidden',
  "after:content-[''] after:pointer-events-none after:absolute after:inset-0 after:opacity-0",
  'after:bg-glint-subtle after:bg-[length:250%_100%]',
  'hover:after:opacity-100 hover:after:animate-glint',
  'motion-reduce:hover:after:animate-none',
)

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-md transition-colors ring-offset-2 focus-visible disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      // Buttons stay in the console's mono action language. `action` remains as
      // a compatibility alias for existing call sites, but must not switch the
      // shared primitive to Inter. Text treatment is size-driven below: XS uses
      // medium title-case copy, while the larger sizes use tighter uppercase.
      typography: {
        label: 'font-jetbrains',
        action: 'font-jetbrains',
      },
      // No shadows on any variant. The plivo.com kit builds structure from
      // hairline borders and reserves elevation for things that genuinely
      // float — menus, popovers, modals, toasts. A button is not one of them.
      variant: {
        default: cn('bg-primary text-primary-foreground', brandHoverGlint),
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        'destructive-outline': 'text-destructive hover:bg-destructive/10 border border-destructive',
        'success-outline':
          'text-status-success hover:bg-status-success/10 border border-status-success',
        // hover:border-brand too, or the grey input border frames the blue fill.
        outline: cn('border border-input bg-background hover:border-brand', brandHoverGlint),
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        tertiary: 'bg-st2 text-primary hover:bg-st2/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        ghostLink: 'text-primary hover:text-primary/80',
        subtle: 'bg-hv1 text-st2-foreground hover:bg-st2/80',
        success: 'bg-status-success text-white hover:bg-status-success/80',
        dashed:
          'border-input border border-dashed bg-background hover:bg-accent hover:text-accent-foreground gap-2',
        ghostDestructive:
          'text-destructive hover:text-destructive disabled:opacity-50 disabled:hover:text-destructive bg-transparent hover:bg-transparent border-none',
      },
      size: {
        default: 'h-9 px-4 pb-2 pt-2.5 text-label uppercase tracking-[0.05em]',
        sm: 'h-[30px] rounded-md px-3 pt-0.5 text-label uppercase tracking-[0.05em]',
        lg: 'h-10 rounded-md px-8 pt-0.5 text-label uppercase tracking-[0.05em]',
        xl: 'h-12 rounded-md px-8 pt-0.5 text-label uppercase tracking-[0.05em]',
        xs: 'h-6 px-2 pt-0.5 text-xs-500 normal-case tracking-[0.01em]',
        icon: 'h-9 w-9 p-0',
        iconSm: 'h-8 w-8',
        iconXs: 'h-6 w-6',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      typography: 'label',
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  disabledReason?: string
  tooltipContent?: string
  tooltipSide?: 'top' | 'bottom' | 'left' | 'right'
  tooltipAlign?: 'start' | 'center' | 'end'
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, typography, asChild = false, tooltipContent, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    const button = (
      <Comp
        className={cn(buttonVariants({ variant, size, typography, className }))}
        ref={ref}
        {...props}
      />
    )
    if ((!props.disabled || !props.disabledReason) && !tooltipContent) return button
    return (
      <Tooltip
        content={props.disabled ? props.disabledReason : tooltipContent}
        side={props.tooltipSide}
        align={props.tooltipAlign}
      >
        <div className="flex">{button}</div>
      </Tooltip>
    )
  },
)
Button.displayName = 'Button'

const ButtonWithLoader = ({ children, loading, ...props }: ButtonProps & { loading: boolean }) => (
  <Button {...props} disabled={loading || props.disabled}>
    {loading && <Loader2 className={cn(children && 'mr-2', 'h-4 w-4 animate-spin')} />}
    {children}
  </Button>
)

export { Button, ButtonWithLoader, buttonVariants }
